/* ============================================================================
 * ScopeProposal — narrative ("scope-centric") proposal body.
 *
 * The alternative to the line-itemized layout. Instead of qty/unit/rate
 * tables, it renders each section as written scope: the section name as a
 * heading and its line items as plain bullets (H/O-P flagged inline), in a
 * lump-sum "word style" that mirrors K.O. Construction's real proposal doc.
 *
 * Sections are grouped by `section.kind`:
 *   - 'base'      → "Base Scope of Work"          → aggregate "Total Base Cost"
 *   - 'allowance' → "Allowance Items / Variable Scope" → per-section Cost, and
 *                    aggregate "Plus Allowances"
 *
 * This component renders ONLY the scope groups (not the final project total) —
 * each host (EstimatePreview / ClientProposalView) already renders its own
 * authoritative totals block, so we avoid double-totaling.
 *
 * Used by EstimatePreview and ClientProposalView when estimate.layout==='scope'.
 * ========================================================================== */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Layers, Wallet, CheckCircle2, Info, AlertTriangle } from 'lucide-react';
import { computeSectionTotals, formatUsd } from '@/lib/estimatesData';
import { ProposalImage } from '@/components/estimates/ProposalImage';
import { ProposalChart } from '@/components/estimates/ProposalChart';
import { EditableText } from '@/components/estimates/EditableText';

/* Editable-preview plumbing: every component below accepts an optional
 * `edit` bag — { editable, onUpdateSection, onUpdateBlock, onUpdateItem } —
 * passed only by the builder's EstimatePreview. The client portal
 * (ClientProposalView) never passes it, so the portal render is untouched. */
const NO_EDIT = { editable: false };

const HopBadge = () => (
  <Badge
    variant="outline"
    className="ml-2 align-middle bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-400/30 uppercase text-[9px] tracking-[0.16em] shrink-0 not-italic"
  >
    H/O-P
  </Badge>
);

/* --- Rich content blocks (open-doc scope editor) ------------------------- */
const CALLOUT_TONES = {
  included: {
    box: 'border-emerald-500/30 bg-emerald-500/[0.07]',
    title: 'text-emerald-800 dark:text-emerald-300',
    text: 'text-emerald-900/80 dark:text-emerald-100/80',
    Icon: CheckCircle2,
  },
  info: {
    box: 'border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/[0.06]',
    title: 'text-[hsl(var(--primary))]',
    text: 'text-foreground/80',
    Icon: Info,
  },
  warning: {
    box: 'border-amber-500/30 bg-amber-500/[0.07]',
    title: 'text-amber-800 dark:text-amber-300',
    text: 'text-amber-900/80 dark:text-amber-100/80',
    Icon: AlertTriangle,
  },
};

const ScopeBlock = ({ block, sectionId, edit = NO_EDIT }) => {
  const { editable, onUpdateBlock } = edit;
  const patchBlock = (patch) => onUpdateBlock?.(sectionId, block.id, patch);

  switch (block.type) {
    case 'heading':
      return block.text || editable ? (
        <h5 className="mt-3 mb-1 text-sm font-semibold text-foreground break-words">
          {editable ? (
            <EditableText
              editable
              multiline={false}
              value={block.text || ''}
              placeholder="Heading…"
              onCommit={(text) => patchBlock({ text })}
            />
          ) : (
            block.text
          )}
        </h5>
      ) : null;

    case 'paragraph':
      return block.text || editable ? (
        <p className="text-[13px] text-foreground/85 whitespace-pre-wrap leading-relaxed">
          {editable ? (
            <EditableText
              editable
              value={block.text || ''}
              placeholder="Paragraph…"
              onCommit={(text) => patchBlock({ text })}
              as="span"
              className="block whitespace-pre-wrap"
            />
          ) : (
            block.text
          )}
        </p>
      ) : null;

    case 'caveat':
      return block.text || editable ? (
        <p className="text-[12px] italic text-[hsl(var(--destructive))]/85 leading-relaxed">
          {editable ? (
            <EditableText
              editable
              value={block.text || ''}
              placeholder="Caveat…"
              onCommit={(text) => patchBlock({ text })}
              as="span"
              className="block whitespace-pre-wrap"
            />
          ) : (
            block.text
          )}
        </p>
      ) : null;

    case 'bullets': {
      // When editable, keep ORIGINAL indexes so a commit targets the right
      // item; committing an empty string removes the bullet.
      const source = Array.isArray(block.items) ? block.items : [];
      const rows = editable
        ? source.map((text, idx) => ({ text, idx }))
        : source
            .map((text, idx) => ({ text, idx }))
            .filter((r) => r.text && r.text.trim());
      if (rows.length === 0 && !block.title) return null;
      const excluded = block.variant === 'excluded';
      const commitBullet = (idx, text) => {
        const items = [...source];
        if (!text.trim()) items.splice(idx, 1);
        else items[idx] = text;
        patchBlock({ items });
      };
      return (
        <div className="space-y-1">
          {block.title ? (
            <div
              className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${
                excluded
                  ? 'text-[hsl(var(--destructive))]'
                  : 'text-muted-foreground'
              }`}
            >
              {block.title}
            </div>
          ) : null}
          <ul className="space-y-1">
            {rows.map((r) => (
              <li
                key={r.idx}
                className={`flex items-start gap-2 text-sm leading-relaxed ${
                  excluded
                    ? 'text-[hsl(var(--destructive))]/90'
                    : 'text-foreground/90'
                }`}
              >
                <span
                  className={`mt-[2px] shrink-0 ${
                    excluded
                      ? 'text-[hsl(var(--destructive))]/70'
                      : 'text-[hsl(var(--primary))]'
                  }`}
                  aria-hidden
                >
                  {excluded ? '✕' : '•'}
                </span>
                {editable ? (
                  <EditableText
                    editable
                    multiline={false}
                    value={r.text || ''}
                    placeholder="Bullet (empty removes it)…"
                    onCommit={(text) => commitBullet(r.idx, text)}
                    className="break-words flex-1 min-w-0"
                  />
                ) : (
                  <span className="break-words">{r.text}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      );
    }

    case 'image': {
      if (!block.url) return null;
      return (
        <figure className="print-avoid" data-testid="scope-image">
          <ProposalImage
            url={block.url}
            path={block.path}
            alt={block.caption || 'Proposal image'}
            className={`w-full rounded-md border border-border ${
              block.fit === 'cover'
                ? 'h-56 object-cover'
                : 'max-h-[420px] object-contain bg-[hsl(var(--muted))]/30'
            }`}
            loading="lazy"
          />
          {block.caption ? (
            <figcaption className="mt-1.5 text-[12px] italic text-muted-foreground text-center">
              {block.caption}
            </figcaption>
          ) : null}
        </figure>
      );
    }

    case 'chart':
      return <ProposalChart block={block} />;

    case 'table': {
      const columns = Array.isArray(block.columns) ? block.columns : [];
      const rows = Array.isArray(block.rows) ? block.rows : [];
      if (columns.length === 0 || rows.length === 0) return null;
      const lastIdx = columns.length - 1;
      const rightLast = block.numericLast !== false;
      const alignFor = (i) =>
        rightLast && i === lastIdx ? 'text-right tabular-nums' : 'text-left';
      return (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[hsl(var(--muted))]/50">
                {columns.map((c, i) => (
                  <th
                    key={i}
                    className={`px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold ${alignFor(i)}`}
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} className="border-t border-border">
                  {columns.map((_, ci) => (
                    <td
                      key={ci}
                      className={`px-3 py-2 text-foreground/90 ${alignFor(ci)} ${
                        rightLast && ci === lastIdx ? 'font-semibold' : ''
                      }`}
                    >
                      {row?.[ci] || ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    case 'callout': {
      if (!block.title && !block.text && !editable) return null;
      const tone = CALLOUT_TONES[block.tone] || CALLOUT_TONES.info;
      const { Icon } = tone;
      return (
        <div className={`rounded-md border px-3.5 py-3 ${tone.box}`}>
          {block.title || editable ? (
            <div
              className={`flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.14em] ${tone.title}`}
            >
              <Icon size={13} />
              {editable ? (
                <EditableText
                  editable
                  multiline={false}
                  value={block.title || ''}
                  placeholder="Callout title…"
                  onCommit={(title) => patchBlock({ title })}
                  className="flex-1 min-w-0"
                />
              ) : (
                block.title
              )}
            </div>
          ) : null}
          {block.text || editable ? (
            <div
              className={`${block.title || editable ? 'mt-1.5' : ''} text-[13px] whitespace-pre-wrap leading-relaxed ${tone.text}`}
            >
              {editable ? (
                <EditableText
                  editable
                  value={block.text || ''}
                  placeholder="Callout text…"
                  onCommit={(text) => patchBlock({ text })}
                  as="span"
                  className="block whitespace-pre-wrap"
                />
              ) : (
                block.text
              )}
            </div>
          ) : null}
        </div>
      );
    }

    default:
      return null;
  }
};

export const ScopeBlocks = ({ blocks, sectionId, edit = NO_EDIT }) => {
  const list = Array.isArray(blocks) ? blocks : [];
  if (list.length === 0) return null;
  return (
    <div className="space-y-2.5" data-testid="scope-blocks">
      {list.map((b) => (
        <ScopeBlock key={b.id} block={b} sectionId={sectionId} edit={edit} />
      ))}
    </div>
  );
};

/* Bulleted scope items for one section (lump-sum — no per-line pricing). */
const ScopeItems = ({ section, edit = NO_EDIT }) => {
  const { editable, onUpdateItem, onUpdateSection } = edit;
  const items = Array.isArray(section?.items) ? section.items : [];
  const blocks = Array.isArray(section?.blocks) ? section.blocks : [];
  const hasNotes = !!(section?.notes && section.notes.trim());
  if (items.length === 0 && blocks.length === 0 && !hasNotes) {
    return (
      <p className="mt-1.5 text-[13px] italic text-muted-foreground/70">
        Scope details to be finalized.
      </p>
    );
  }
  return (
    <div className="mt-1.5 space-y-2.5">
      <ScopeBlocks blocks={blocks} sectionId={section.id} edit={edit} />
      {items.length > 0 ? (
        <ul className="mt-1.5 space-y-1" data-testid={`scope-items-${section.id}`}>
          {items.map((it) => (
            <li
              key={it.id}
              className="flex items-start gap-2 text-sm text-foreground/90 leading-relaxed"
            >
              <span className="text-[hsl(var(--primary))] mt-[2px] shrink-0" aria-hidden>
                •
              </span>
              {editable ? (
                <span className="break-words flex-1 min-w-0">
                  <EditableText
                    editable
                    multiline={false}
                    value={it.desc || ''}
                    placeholder="Scope line…"
                    onCommit={(desc) => onUpdateItem?.(section.id, it.id, { desc })}
                  />
                  {it.ho_pick ? <HopBadge /> : null}
                </span>
              ) : (
                <span className="break-words">
                  {it.desc || '—'}
                  {it.ho_pick ? <HopBadge /> : null}
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : null}
      {hasNotes || editable ? (
        <p className="mt-2 text-[13px] text-foreground/70 whitespace-pre-wrap leading-relaxed">
          {editable ? (
            <EditableText
              editable
              value={section?.notes || ''}
              placeholder="Section notes…"
              onCommit={(notes) => onUpdateSection?.(section.id, { notes })}
              as="span"
              className="block whitespace-pre-wrap"
            />
          ) : (
            section.notes
          )}
        </p>
      ) : null}
    </div>
  );
};

/* One grouped card — the base group or the allowance group. */
const ScopeGroup = ({
  title,
  icon: Icon,
  sections,
  aggregateLabel,
  aggregateValue,
  showPerSectionCost = false,
  tone = 'primary',
  testId,
  edit = NO_EDIT,
}) => {
  if (!Array.isArray(sections) || sections.length === 0) return null;

  const isAmber = tone === 'amber';
  const headBorder = isAmber ? 'border-amber-400/30' : 'border-border';
  const aggWrap = isAmber
    ? 'border-amber-400/40 bg-amber-500/10'
    : 'border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10';
  const aggLabel = isAmber
    ? 'text-amber-800/90 dark:text-amber-200/90'
    : 'text-[hsl(var(--primary))]/90';
  const aggValue = isAmber ? 'text-amber-800 dark:text-amber-200' : 'text-[hsl(var(--primary))]';

  return (
    <div
      className={`rounded-md border overflow-hidden ${isAmber ? 'border-amber-400/30' : 'border-border'}`}
      data-testid={testId}
    >
      <div
        className={`flex items-center gap-2 px-4 py-2.5 border-b ${headBorder} bg-[hsl(var(--muted))]/40`}
      >
        <Icon size={14} className={isAmber ? 'text-amber-700 dark:text-amber-300' : 'text-[hsl(var(--primary))]'} />
        <h3 className="text-[11px] sm:text-xs uppercase tracking-[0.22em] font-semibold text-foreground">
          {title}
        </h3>
      </div>

      <div className="px-4 py-4 space-y-5">
        {sections.map((section) => {
          const st = computeSectionTotals(section);
          return (
            <div key={section.id} data-testid={`scope-section-${section.id}`}>
              <div className="flex items-baseline justify-between gap-3">
                <h4 className="text-sm font-semibold text-foreground break-words flex-1 min-w-0">
                  {edit.editable ? (
                    <EditableText
                      editable
                      multiline={false}
                      value={section.name || ''}
                      placeholder="Section name…"
                      onCommit={(name) =>
                        edit.onUpdateSection?.(section.id, { name })
                      }
                    />
                  ) : (
                    section.name || 'Section'
                  )}
                </h4>
                {showPerSectionCost ? (
                  <span
                    className="text-sm font-semibold text-foreground tabular-nums shrink-0"
                    data-testid={`scope-section-cost-${section.id}`}
                  >
                    {formatUsd(st.section_display_total)}
                  </span>
                ) : null}
              </div>
              <ScopeItems section={section} edit={edit} />
            </div>
          );
        })}
      </div>

      <div
        className={`border-t px-4 py-3 flex items-center justify-between ${aggWrap}`}
      >
        <span
          className={`text-[11px] sm:text-xs uppercase tracking-[0.22em] font-semibold ${aggLabel}`}
        >
          {aggregateLabel}
        </span>
        <span
          className={`text-lg sm:text-xl font-bold tabular-nums ${aggValue}`}
          data-testid={`${testId}-total`}
        >
          {formatUsd(aggregateValue)}
        </span>
      </div>
    </div>
  );
};

/**
 * Scope-centric proposal body — base + allowance groups only.
 *
 * Props:
 *   estimate — hydrated estimate row (sections, kind, etc.)
 *   totals   — computed totals (base_sections_total / allowance_sections_total)
 */
export const ScopeProposalBody = ({ estimate, totals, edit = NO_EDIT }) => {
  const sections = Array.isArray(estimate?.sections) ? estimate.sections : [];
  const baseSections = sections.filter((s) => s?.kind !== 'allowance');
  const allowanceSections = sections.filter((s) => s?.kind === 'allowance');

  if (sections.length === 0) {
    return (
      <div
        className="rounded-md border border-dashed border-border bg-[hsl(var(--muted))]/40 px-4 py-6 text-center text-sm text-muted-foreground"
        data-testid="scope-proposal-empty"
      >
        Scope details haven&apos;t been added yet.
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="scope-proposal-body">
      <ScopeGroup
        title="Base Scope of Work"
        icon={Layers}
        sections={baseSections}
        aggregateLabel="Total Base Cost"
        aggregateValue={totals?.base_sections_total}
        tone="primary"
        testId="scope-group-base"
        edit={edit}
      />
      <ScopeGroup
        title="Allowance Items / Variable Scope"
        icon={Wallet}
        sections={allowanceSections}
        aggregateLabel="Plus Allowances"
        aggregateValue={totals?.allowance_sections_total}
        showPerSectionCost
        tone="amber"
        testId="scope-group-allowance"
        edit={edit}
      />
    </div>
  );
};

export default ScopeProposalBody;
